import { get } from "lodash-es";
import prettyBytes from "pretty-bytes";
import {
  type FC,
  Suspense,
  lazy,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { Doc } from "sharedb/lib/client";
import { twMerge } from "tailwind-merge";

import type {
  FileCellType,
  FileDataType,
  PathType,
  PdfState,
  WorksheetType,
} from "../../../shared/types/worksheet";
import { createId } from "../../../shared/utils";

import { PdfPageToolbar } from "./PdfPageToolbar";
import VideoAsset from "./VideoAsset";
import { ZoomToolbar } from "./ZoomToolbar";

import { collaborationUploadFile, deleteUploadedFile } from "api/api-worksheets";
import { t } from "i18n/i18n";
import { FileUploadField } from "mds/components/FileUploadField";
import { LegacyButton } from "mds/components/LegacyButton";
import { showConfirmationModal } from "mds/components/Modal";
import { useHoverable } from "mds/hooks/use-hoverable";
import { useIsMdOrLarger } from "mds/hooks/use-responsive";
import { CircleSuccessGreenIcon, LoadingDotsIcon, TrashDeleteIcon } from "mds/icons";
import { DocViewContext } from "providers/DocViewProvider";
import { toastLocalizedOperationError } from "utils/alerts";
import { BUTTON_EVENT_CATEGORY, getPageFieldProps, trackEvent } from "utils/amplitude";
import { getDefaultPdfState } from "utils/asset";
import { isImageFile, isPDFFile, isVideoFile } from "worksheets/helpers/files";

const PdfAsset = lazy(() => import("./PdfAsset"));

interface FileType {
  type?: string;
  path?: string;
  size?: number;
}

const UPLOAD_SUCCESS_MESSAGE_DURATION = 3000;
const MAX_FILES_WITHOUT_CONFIRMATION = 10;
const MAX_FILES_ALLOWED = 25;

const uploadFile = async (file: FileType, doc: Doc<WorksheetType>, path: PathType) => {
  const size = prettyBytes(file.size);
  const id = createId();
  const partialFileData: Omit<FileDataType, "url"> = {
    placeholder: true,
    t: file.type,
    filename: file.path || id,
    size,
    id,
  };
  // Submit placeholder
  doc.submitOp({ p: [...path, 99999], li: partialFileData });

  const { url: fileUrl } = await collaborationUploadFile(file as Blob as File);

  const newFileData: FileDataType = { ...partialFileData, url: fileUrl };
  delete newFileData.placeholder;
  const fileData = get(doc.data, path) as FileDataType[];
  const placeholder = fileData.find((fd) => fd.id === newFileData.id);
  const placeholderIndex = fileData.indexOf(placeholder);

  // Replace placeholder with actual image URL
  if (placeholder) {
    doc.submitOp({
      p: [...path, placeholderIndex],
      li: newFileData,
      ld: placeholder,
    });
  } else {
    // If the placeholder got deleted during upload, we still just insert the image
    doc.submitOp({
      p: [...path, 9999],
      li: newFileData,
    });
  }
};

const onDrop = (files: FileType[], doc: Doc<WorksheetType>, path: PathType) => {
  Promise.all(files.map((file) => uploadFile(file, doc, path))).catch(() => {
    const fileData = get(doc.data, path) as FileDataType[];
    const placeholders = fileData.filter((fd) => fd.placeholder);
    const ops = placeholders.map((p) => ({ p: [...path, fileData.indexOf(p)], ld: p }));
    doc.submitOp(ops);
    toastLocalizedOperationError("failed_to_upload_file");
  });
};

interface FilePreviewProps {
  className?: string;
  // TODO: Rollup-MVP: We used to enabled condensed mode for rollups due to network considerations
  // but chose to disable it for now to be more intuitive for testing.
  condensed?: boolean;
  id?: string;
  readOnly?: boolean;
  fileData: FileDataType;
  shouldSyncWithInstructorView: boolean;
  onDelete: () => void;
  maxWidth?: number;
}

const FilePreview: FC<FilePreviewProps> = ({
  fileData,
  onDelete,
  id,
  className,
  condensed = false,
  shouldSyncWithInstructorView,
  readOnly,
  maxWidth,
}) => {
  // `placeholder` is true if the file is actively being uploaded (and may end up in state if the upload fails?).
  const { placeholder, url } = fileData;
  const isImage = isImageFile(fileData.t);
  const isVideo = isVideoFile(fileData.t);
  const isPDF = isPDFFile(fileData.t);
  const [recentlyUploaded, setRecentlyUploaded] = useState(placeholder);
  const showUploadSuccessful = recentlyUploaded && !placeholder;
  const filePreviewHover = useHoverable();
  const isSmallScreen = !useIsMdOrLarger();

  // The filename are often (always?) prefixed with a "./" to the start of their filename, which we want to avoid
  // displaying to the user given this won't make sense to them.
  const trimmedFileName = fileData.filename?.startsWith("./")
    ? fileData.filename.slice(2)
    : fileData.filename;

  useEffect(() => {
    let timeout: NodeJS.Timeout;
    if (showUploadSuccessful) {
      timeout = setTimeout(() => {
        setRecentlyUploaded(false);
      }, UPLOAD_SUCCESS_MESSAGE_DURATION); // disable notification after 3 seconds
    }
    return () => clearTimeout(timeout);
  }, [showUploadSuccessful, setRecentlyUploaded]);

  // These are for PDFs only
  const [pdfState, setPdfState] = useState<PdfState>(getDefaultPdfState());

  const onPdfStateChange = (newState: Partial<PdfState>) => {
    setPdfState((prevState) => ({
      ...prevState,
      ...newState,
    }));
  };

  const pdfTools = (
    <>
      <ZoomToolbar setZoom={(zoom) => onPdfStateChange({ zoom })} zoom={pdfState.zoom} />

      {pdfState.totalPages > 1 && (
        <PdfPageToolbar pdfState={pdfState} setPdfState={onPdfStateChange} />
      )}
    </>
  );

  return (
    <div
      // The 26 extra pixels are the height of the top bar for the preview
      className={twMerge("shadow-level-3", className)}
      {...filePreviewHover.hoverParentProps}
    >
      <div className="body-s flex w-full items-center px-4 pt-4">
        {placeholder ? (
          <span className="font-bold">
            {t("file_field.uploading_filename", { filename: trimmedFileName })}
          </span>
        ) : (
          <a
            className="truncate font-bold text-blue-shade-20"
            href={fileData.url}
            rel="noopener noreferrer"
            target="_blank"
          >
            {trimmedFileName}
          </a>
        )}

        {/* Avoid shrinking the size display as we want the full value to render properly. */}
        <span className="flex-shrink-0 pl-1 text-black-tint-40">({fileData.size})</span>

        {placeholder && <LoadingDotsIcon />}

        {!condensed && (
          <div className="ml-auto flex items-center gap-2">
            {showUploadSuccessful && (
              <span className="left-0 mx-4 flex items-center text-black-tint-40">
                <CircleSuccessGreenIcon size="xs" />
                <span className="ml-2">{t("common.uploaded")}</span>
              </span>
            )}

            {isPDF && !shouldSyncWithInstructorView && !isSmallScreen ? pdfTools : null}

            {!readOnly && (
              <LegacyButton
                eventName="File upload - Delete uploaded asset"
                kind="destructive"
                size="s"
                title={t("common.delete")}
                iconOnly
                onClick={onDelete}
              >
                <TrashDeleteIcon />
              </LegacyButton>
            )}
          </div>
        )}
      </div>

      {!condensed && isPDF && isSmallScreen && (
        <div className="flex items-center gap-2 px-4">{pdfTools}</div>
      )}

      {!condensed && isImage && url && (
        <div className="flex justify-center p-4">
          <img
            alt={t("file_field.uploaded_image")}
            className="h-auto w-auto max-w-full object-contain"
            src={url}
          />
        </div>
      )}

      {!condensed && isVideo && url && (
        <div className="p-2">
          <VideoAsset
            className="w-full"
            id={id}
            shouldSyncWithInstructorView={shouldSyncWithInstructorView}
            url={url}
          />
        </div>
      )}

      {!condensed && isPDF && url && (
        // Because PDFs are zoomable, the preview width need needs to be constant;
        // otherwise, zooming in will make the preview wider, which will change the rendered PDF width.
        // Setting the width in CSS based on the maxWidth of the container
        // ensures that zooming works consistently.
        <div className="p-2" style={maxWidth ? { width: `${maxWidth}px` } : undefined}>
          <Suspense fallback={<LoadingDotsIcon />}>
            <PdfAsset
              pdfState={pdfState}
              setPdfState={onPdfStateChange}
              shouldSyncWithInstructorView={shouldSyncWithInstructorView}
              url={url}
            />
          </Suspense>
        </div>
      )}
    </div>
  );
};

interface FileFieldProps {
  doc: Doc<WorksheetType>;
  path: PathType;
  id: string;
  m: boolean;
}

export const FileField: FC<FileFieldProps> = ({ doc, path, id, m: multiple }) => {
  const ref = useRef<HTMLDivElement>(null);
  const fileData = get(doc.data, path) as FileCellType;
  const { showResponses, readOnly, isProjecting, canInteractAsStudent, pageId } =
    useContext(DocViewContext);
  // `fileData` should be [] on a blank field in normal usage, though we have safeguards to prevent
  // issues if it's undefined, which seem to have occurred sporadically and caused bugs in the past.
  const hasNoFile = fileData && fileData.length <= 0;

  const deleteFile = (index: number) => {
    const fd = get(doc.data, path, index) as FileDataType;
    if (fd && fd.id && fd.url) {
      deleteUploadedFile(fd.id).catch(() =>
        toastLocalizedOperationError("failed_to_delete_uploaded_file"),
      );
    }
    doc.submitOp({ p: [...path, index], ld: fd });
  };

  const onFileDrop = useCallback(
    async (droppedFiles: File[]) => {
      // Check if exceeding maximum file limit
      if (droppedFiles.length > MAX_FILES_ALLOWED) {
        await showConfirmationModal({
          title: t("file_field.too_many_files_title"),
          labelText: t("file_field.too_many_files_message", {
            count: droppedFiles.length,
            max: MAX_FILES_ALLOWED,
          }),
          confirmButtonText: t("common.ok"),
          hideCancelButton: true,
          confirmButtonKind: "primary",
        });
        return; // Reject the upload
      }

      // Show confirmation if uploading many files
      if (droppedFiles.length > MAX_FILES_WITHOUT_CONFIRMATION) {
        const confirmed = await showConfirmationModal({
          title: t("file_field.confirm_many_files_title", {
            count: droppedFiles.length,
          }),
          labelText: t("file_field.confirm_many_files_message", {
            count: droppedFiles.length,
          }),
          confirmButtonText: t("file_field.confirm_upload"),
          confirmButtonKind: "primary",
        });

        if (!confirmed) {
          return; // User cancelled
        }
      }

      onDrop(droppedFiles, doc, path);
      trackEvent("File upload block", {
        ...getPageFieldProps(pageId, path),
        ...BUTTON_EVENT_CATEGORY,
      });
    },
    [doc, path, pageId],
  );
  return (
    <div ref={ref}>
      {/* Image List */}
      {!hasNoFile && fileData && (
        <div
          className={
            showResponses ? "flex max-w-full flex-wrap items-start justify-start gap-4" : ""
          }
        >
          {fileData.map((fd, i) => (
            <FilePreview
              // It's extremely important that the key contains a unique ID that is preserved
              // during all upload stages, otherwise render order and callback association gets jumbled
              className={!showResponses ? "mb-4" : undefined}
              fileData={fd}
              id={id}
              key={`img-${id}-${fd.id}`}
              maxWidth={ref.current?.clientWidth}
              readOnly={readOnly}
              shouldSyncWithInstructorView={isProjecting}
              onDelete={() => deleteFile(i)}
            />
          ))}
        </div>
      )}

      {/* Upload Zone – this should never be shown on Class Responses view, but we do want it to appear when
          featured as a block if there is no file, to avoid giving the impression of a bug in the UI.
           However, we avoid making the drop zone appear when featured if there is already a file */}
      {(!fileData || hasNoFile || (multiple && !showResponses)) && showResponses !== "ALL" && (
        <FileUploadField
          disabled={isProjecting || !canInteractAsStudent || !fileData}
          multiple={multiple}
          name={`file-${id}`}
          onDrop={onFileDrop}
        />
      )}
    </div>
  );
};
